import { isPhoneE164 } from 'shared/helpers/Validators';

// Reservation phones arrive pre-validated by the PMS middleware's shared-lib
// normalizer (PMSMW PRO-360, June 2026): either absent or valid E.164 digits
// (country code included, no '+', no separators). Do NOT re-validate with
// libphonenumber — its metadata is stricter than the BE validator and silently
// dropped BE-approved numbers. Only prepend the '+' and check E.164 structure.
const normalizePhone = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const candidate = trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
  return isPhoneE164(candidate) ? candidate : null;
};

// Parse a date-ish value to epoch ms, treating missing OR unparseable values as
// +Infinity so they sort last and never yield NaN from a comparator.
const toTime = (v) => {
  if (!v) return Number.POSITIVE_INFINITY;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};

const buildRow = (reservation, stay, guest, normalized) => {
  const firstName = guest.firstName || '';
  const lastName = guest.lastName || '';
  const name = `${firstName} ${lastName}`.trim() || normalized;
  return {
    name,
    firstName,
    lastName,
    phone: normalized,
    email: guest.email || null,
    arrival: stay.arrivalDateTime || null,
    departure: stay.departureDateTime || null,
    room: stay?.accommodation?.roomNumber || null,
    chatlynBookingId: reservation.chatlynBookingId,
    pmsBookingId: reservation.pmsBookingId || null,
    preferredLanguage: reservation.preferredLanguage || null,
  };
};

/**
 * Flatten a list of reservations into one row per guest, keyed and deduped by
 * E.164 phone. Rules:
 *   - Guests whose phone is missing or fails the structural E.164 check are
 *     dropped (silent — no caller signal yet).
 *   - When the same phone appears across multiple reservations or stays, the
 *     row with the latest `arrivalDateTime` wins. Any-arrival rows always beat
 *     no-arrival rows.
 */
export const flattenReservationsToGuestRows = (reservations = []) => {
  const byPhone = new Map();
  reservations.forEach((reservation) => {
    (reservation.stayDetails || []).forEach((stay) => {
      (stay.guests || []).forEach((guest) => {
        const normalized = normalizePhone(guest.phone);
        if (!normalized) return;
        const row = buildRow(reservation, stay, guest, normalized);
        const existing = byPhone.get(normalized);
        if (!existing) {
          byPhone.set(normalized, row);
          return;
        }
        if (existing.arrival && !row.arrival) return;
        if (!existing.arrival && row.arrival) {
          byPhone.set(normalized, row);
          return;
        }
        if (
          row.arrival &&
          existing.arrival &&
          toTime(row.arrival) > toTime(existing.arrival)
        ) {
          byPhone.set(normalized, row);
        }
      });
    });
  });
  return Array.from(byPhone.values());
};

const compareDates = (a, b) => {
  const ta = toTime(a);
  const tb = toTime(b);
  // Equal handles two +Infinity (both missing/invalid) → 0, avoiding the
  // Infinity - Infinity = NaN trap that destabilizes Array.sort.
  if (ta === tb) return 0;
  return ta < tb ? -1 : 1;
};

const compareStrings = (a, b) => {
  const va = a == null ? '' : String(a);
  const vb = b == null ? '' : String(b);
  if (va === '' && vb !== '') return 1;
  if (vb === '' && va !== '') return -1;
  return va.localeCompare(vb, undefined, { sensitivity: 'base' });
};

export const sortRows = (rows, field = 'arrival', direction = 'asc') => {
  const copy = [...rows];
  const cmp =
    field === 'arrival' || field === 'departure'
      ? compareDates
      : compareStrings;
  copy.sort((a, b) => cmp(a[field], b[field]));
  return direction === 'desc' ? copy.reverse() : copy;
};

export const filterRows = (rows, query) => {
  const q = (query || '').trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const name = (row.name || '').toLowerCase();
    const phone = (row.phone || '').toLowerCase();
    return name.includes(q) || phone.includes(q);
  });
};

const hashBookingIdToInt = (bookingId) => {
  const source = String(bookingId || '');
  if (!source) return 0;
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) % 1e10;
  }
  return hash;
};

/**
 * Build a synthetic ContactListContactDto that satisfies the simplified send
 * endpoint's class-validator rules without referencing a real Chatlyn contact.
 *
 * `id` is fabricated from a hash of the booking id plus a 4-digit random
 * suffix. The BE treats `id` as cosmetic on this code path (logging +
 * `%%contact.id%%` placeholder substitution only; no DB lookup, idempotency,
 * or audit-trail use — see ChatlynInboxMW template-placeholder.service.ts and
 * the absence of WhatsAppContactService in the list-send flow).
 *
 * Caller contract: `row.phone_number` must already be in E.164 form with the
 * leading `+`. Callers spreading an in-house row should rename `phone` → `phone_number`.
 */
export const buildSyntheticContact = (row, fallbackLanguage, accountId) => {
  const base = hashBookingIdToInt(row.chatlynBookingId);
  const id = base * 10000 + Math.floor(Math.random() * 10000);
  return {
    id,
    name: row.name || 'Guest',
    phone_number: row.phone_number,
    account_id: accountId,
    custom_attributes: {
      ch_pref_lang: row.preferredLanguage || fallbackLanguage,
    },
  };
};
